import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import './styles/global.css';
import './styles/CalibrationWizard.css';
import Sidebar from './components/Sidebar';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import LoadingScreen from './components/LoadingScreen';
import AppErrorBoundary from './components/AppErrorBoundary';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Trends from './pages/Trends';
import Settings from './pages/Settings';
import Alerts from './pages/Alerts';
import ActivatePlus from './pages/ActivatePlus';
import Devices from './pages/Devices';
import Admin from './pages/Admin';
import CalibrationWizard from './pages/CalibrationWizard';
import ClaimDevice from './pages/ClaimDevice';
import Help from './pages/Help';
import InstallGuide from './pages/InstallGuide';

// The public legal / opt-in pages live as static HTML in /public — those are
// the exact copies submitted for A2P 10DLC vetting, so they are the single
// source of truth. These routes just forward the extension-less paths to the
// approved .html so any registered or shared link keeps working.
function StaticRedirect({ to }) {
  window.location.replace(to);
  return null;
}

// Same allowlist as Login.js — only honor next= values that look
// like local app paths.
function safeNextPath(raw) {
  if (!raw) return null;
  if (!/^\/[a-zA-Z]/.test(raw)) return null;
  return raw;
}

// Wrapper: redirects to /login if not authenticated.
// Preserves the current path + query string as ?next= so that after
// login the user lands back where they were trying to go (essential
// for QR deeplinks like /claim?d=...&t=...).
function PrivateRoute({ user, children }) {
  const location = useLocation();
  if (user === null) {
    const fullPath = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(fullPath)}`} replace />;
  }
  return children;
}

// Wrapper: if already logged in, redirect away from the login page.
// Honors ?next= so a logged-in user who lands on /login?next=/claim?d=...
// (e.g. from a stale tab or external link) doesn't get silently
// dropped at /dashboard.
function PublicRoute({ user, children }) {
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));
  if (user !== null) return <Navigate to={nextPath || '/dashboard'} replace />;
  return children;
}

// ── Admin route ──────────────────────────────────────────────
//
// Gated on the REAL `admin` custom claim, not on an email string.
//
// The server never trusted the email: all seven admin callables start with
//   if (!(request.auth.token && request.auth.token.admin === true)) throw ...
// and Admin.js reads nothing from Firestore directly, so the data boundary was
// always the claim. This wrapper used to compare user.email to a hardcoded
// constant, which disagreed with that boundary in two ways:
//
//   1. Changing the account's email address -- which the app now supports --
//      kept the claim but lost the UI, with nothing explaining why.
//   2. user.email is nullable in the Auth SDK; .toLowerCase() on null threw and
//      took out the error boundary instead of redirecting.
//
// Checking the claim makes client and server agree on one fact.
//
// A denial RENDERS rather than redirects. Bouncing to /dashboard was
// indistinguishable from a bug; a page that says what happened is honest, and
// the data stays protected either way because the callables do the real work.
function AdminDenied() {
  return (
    <div className="admin-denied">
      <h1>Admin access required</h1>
      <p>This page is for SoKo Water Systems staff. Your account is not an admin.</p>
      <p><a href="/dashboard">Go to your dashboard</a></p>
    </div>
  );
}

function AdminRoute({ user, children }) {
  // undefined = still resolving the claim, true/false = resolved.
  const [isAdmin, setIsAdmin] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setIsAdmin(false); return undefined; }

    (async () => {
      try {
        // Cached token first: no network, and it is what the callables will
        // see anyway.
        let res = await user.getIdTokenResult();
        // A claim granted after this token was minted is not in it yet. One
        // forced refresh before denying, so set-admin.js does not appear to
        // have failed for an hour. Not a security decision -- the server
        // re-checks the claim on every call regardless.
        if (res.claims.admin !== true) res = await user.getIdTokenResult(true);
        if (!cancelled) setIsAdmin(res.claims.admin === true);
      } catch (err) {
        // Offline, or a refresh that could not reach Google. Fail CLOSED.
        console.error('AdminRoute: could not read admin claim', err);
        if (!cancelled) setIsAdmin(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin === undefined) return <LoadingScreen message="Checking access…" />;
  return isAdmin ? children : <AdminDenied />;
}

export default function App() {
  // user: null = not logged in, undefined = still loading, object = logged in
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    // Tracks which uid we've already run pending-share reconciliation
    // for this page-load, so we don't re-run it on every auth event
    // (token refreshes also fire onAuthStateChanged).
    let reconciledUid = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser || null);

      if (firebaseUser && reconciledUid !== firebaseUser.uid) {
        reconciledUid = firebaseUser.uid;
        // Convert any pending shares addressed to this user's email
        // into real claim docs. This is what makes "share with
        // someone who doesn't have an account yet" work: they sign
        // up, this runs, the device appears. Fire-and-forget; a
        // failure just means it retries on next login.
        try {
          const claimPending = httpsCallable(functions, 'claimPendingShares');
          const res = await claimPending();
          if (res?.data?.claimed > 0) {
            // A device was just granted. Reload once so whatever
            // page they're on re-fetches its device list. This is a
            // blunt approach but it's reliable across all pages and
            // happens at most once per sign-in.
            window.location.reload();
          }
        } catch (err) {
          console.error('claimPendingShares failed:', err);
        }
      }
    });
    return unsubscribe;
  }, []);

  // Show loading screen while Firebase checks auth state. The watchdog inside
  // LoadingScreen reveals a Reload button if auth never settles.
  if (user === undefined) return <LoadingScreen message="Loading…" />;

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      {/* Desktop-only sidebar. Renders once for all routes; hides itself on
          full-bleed flows and below 1024px. See components/Sidebar.js. */}
      <Sidebar user={user} />

      <VerifyEmailBanner user={user} />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          <PublicRoute user={user}>
            <Login />
          </PublicRoute>
        } />

        {/* Firebase email action handler (password reset, email verify).
            Public — a locked-out user isn't signed in. Firebase's custom
            action URL points here: /auth/action?mode=…&oobCode=… */}
        <Route path="/auth/action" element={<ResetPassword />} />

        {/* Public legal / opt-in pages — the A2P-vetted copies are the static
            .html in /public. Forward the extension-less paths to them so any
            registered or shared link still resolves to the approved copy. */}
        <Route path="/privacy" element={<StaticRedirect to="/privacy.html" />} />
        <Route path="/terms" element={<StaticRedirect to="/terms.html" />} />
        <Route path="/text-alerts" element={<StaticRedirect to="/text-alerts.html" />} />

        {/* Public install / connect guide — reachable without auth so it can be
            linked from the claim screen and the dashboard offline banner. */}
        <Route path="/install" element={<InstallGuide />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={
          <PrivateRoute user={user}>
            <Dashboard user={user} />
          </PrivateRoute>
        } />

        <Route path="/trends" element={
          <PrivateRoute user={user}>
            <Trends user={user} />
          </PrivateRoute>
        } />

        <Route path="/settings" element={
          <PrivateRoute user={user}>
            <Settings user={user} />
          </PrivateRoute>
        } />

        <Route path="/help" element={
          <PrivateRoute user={user}>
            <Help />
          </PrivateRoute>
        } />

        <Route path="/alerts" element={
          <PrivateRoute user={user}>
            <Alerts user={user} />
          </PrivateRoute>
        } />

        {/* Activate the included year of Plus — the last step after claiming. */}
        <Route path="/plus/:deviceId" element={
          <PrivateRoute user={user}>
            <ActivatePlus user={user} />
          </PrivateRoute>
        } />

        <Route path="/devices" element={
          <PrivateRoute user={user}>
            <Devices user={user} />
          </PrivateRoute>
        } />

        {/* Device claim — /claim?d=...&t=... is the QR deeplink (flow A,
            auto-claim), /setup is the type-it-in fallback. Same
            component renders both; ClaimDevice branches on URL params. */}
        <Route path="/claim" element={
          <PrivateRoute user={user}>
            <ClaimDevice user={user} />
          </PrivateRoute>
        } />
        <Route path="/setup" element={
          <PrivateRoute user={user}>
            <ClaimDevice user={user} />
          </PrivateRoute>
        } />

        {/* Calibration wizard — tank param optional */}
        <Route path="/setup/:deviceId" element={
          <PrivateRoute user={user}>
            <CalibrationWizard user={user} />
          </PrivateRoute>
        } />
        <Route path="/setup/:deviceId/:tank" element={
          <PrivateRoute user={user}>
            <CalibrationWizard user={user} />
          </PrivateRoute>
        } />

        {/* Admin only */}
        <Route path="/admin" element={
          <AdminRoute user={user}>
            <Admin user={user} />
          </AdminRoute>
        } />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}
